/**
 * DossierCard - admin-only Home tile that generates + downloads the
 * audit dossier zip from GET /v1/admin/exports/dossier.zip.
 *
 * Visibility: returns null for non-admin users (mirrors how the
 * cascade card returns null on error -- Home stays clean for users
 * who shouldn't see admin surfaces). The composition root mounts
 * this unconditionally; the card decides whether to render.
 *
 * Form: since / until / as_of (all datetime-local) + mvb_only
 * checkbox. Defaults: empty fields, which the backend then resolves
 * to its own defaults (until=NOW, since=NOW-30d). Operator can
 * override any of them.
 *
 * Build takes 30-60 seconds against a real dataset; the button
 * surfaces an explicit "Generating dossier..." progress hint so the
 * operator doesn't think it's stuck.
 *
 * Errors:
 *   * 503 chain-broken -- render with the runbook hint baked in.
 *     This is a P0 integrity emergency; the message tells the
 *     operator NOT to retry.
 *   * Other -- generic "Couldn't generate dossier" with the
 *     backend's detail string.
 *
 * Lives under shared/home/ (not under dossier/, which doesn't exist
 * on the frontend) per backlog #454 -- a symmetric frontend
 * dossier/ module lands when a future component needs cross-domain
 * imports for dossier-related UX.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDateInputLang } from "shared/i18n/dateInputLang";

import { useMe } from "shared/api/useMe";
import {
  DossierDownloadError,
  useDownloadDossier,
} from "shared/api/useDownloadDossier";
import { Button } from "shared/components/Button";
import { Card } from "shared/components/Card";
import { MarsadIcon } from "shared/components/MarsadIcon";

function _isoFromLocalInput(value: string): string | undefined {
  // <input dir="auto" type="datetime-local"> emits "YYYY-MM-DDTHH:MM" with no
  // timezone. Treat as UTC so reviewers don't fight tz drift; append
  // :00Z. Same convention v1's DossierCard used.
  if (!value) return undefined;
  return `${value.slice(0, 16)}:00Z`;
}

export function DossierCard() {
  const { t } = useTranslation();
  const dateInputLang = useDateInputLang();
  const me = useMe();
  const download = useDownloadDossier();

  const [since, setSince] = useState<string>("");
  const [until, setUntil] = useState<string>("");
  const [asOf, setAsOf] = useState<string>("");
  const [mvbOnly, setMvbOnly] = useState<boolean>(false);
  const [status, setStatus] = useState<{
    kind: "ok" | "error" | "chain_broken";
    text: string;
  } | null>(null);

  // Non-admin (or still loading) -> nothing rendered. Mirrors
  // PositionQueueWidget visibility model.
  if (!me.data?.is_admin) {
    return null;
  }

  const handleGenerate = async () => {
    setStatus(null);
    try {
      const result = await download.mutateAsync({
        since: _isoFromLocalInput(since),
        until: _isoFromLocalInput(until),
        as_of: _isoFromLocalInput(asOf),
        mvb_only: mvbOnly,
      });
      setStatus({
        kind: "ok",
        text: t("dossier.downloadedOk", { filename: result.filename }),
      });
    } catch (err) {
      if (err instanceof DossierDownloadError && err.isChainBroken()) {
        setStatus({
          kind: "chain_broken",
          text: t("dossier.errorChainBroken", {
            detail: typeof err.detail === "object" ? err.detail.message : err.detail,
          }),
        });
        return;
      }
      const detail =
        err instanceof DossierDownloadError
          ? typeof err.detail === "string"
            ? err.detail
            : err.detail.message
          : err instanceof Error
            ? err.message
            : "unknown error";
      setStatus({
        kind: "error",
        text: t("dossier.errorGeneric", { detail }),
      });
    }
  };

  const statusColour =
    status?.kind === "ok"
      ? "text-rag-green"
      : status?.kind === "chain_broken"
        ? "text-rag-red font-medium"
        : "text-rag-red";

  return (
    <Card testid="dossier-card">
      <header className="mb-2">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ink">
          <MarsadIcon name="dossier" size={18} className="text-brand-brassDark" />
          {t("dossier.heading")}
        </h2>
        <p className="mt-1 text-[12.5px] text-ink-2 max-w-2xl">{t("dossier.body")}</p>
      </header>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-ink-muted">{t("dossier.sinceLabel")}</span>
          <input
            dir="auto"
            type="datetime-local"
            lang={dateInputLang}
            value={since}
            onChange={(e) => setSince(e.target.value)}
            data-testid="dossier-since"
            className="rounded-md border border-paper-2 bg-white px-2 py-1 text-xs focus:border-brand-brass focus:outline-none focus:ring-1 focus:ring-brand-brass"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-ink-muted">{t("dossier.untilLabel")}</span>
          <input
            dir="auto"
            type="datetime-local"
            lang={dateInputLang}
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            data-testid="dossier-until"
            className="rounded-md border border-paper-2 bg-white px-2 py-1 text-xs focus:border-brand-brass focus:outline-none focus:ring-1 focus:ring-brand-brass"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-ink-muted">{t("dossier.asOfLabel")}</span>
          <input
            dir="auto"
            type="datetime-local"
            lang={dateInputLang}
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            data-testid="dossier-asof"
            className="rounded-md border border-paper-2 bg-white px-2 py-1 text-xs focus:border-brand-brass focus:outline-none focus:ring-1 focus:ring-brand-brass"
          />
        </label>
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs text-ink-2">
        <input
          type="checkbox"
          checked={mvbOnly}
          onChange={(e) => setMvbOnly(e.target.checked)}
          data-testid="dossier-mvb-only"
        />
        <span>{t("dossier.mvbOnlyLabel")}</span>
      </label>

      <div className="mt-4 flex items-center gap-3">
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={download.isPending}
          testid="dossier-generate"
        >
          {download.isPending
            ? t("dossier.generatingButton")
            : t("dossier.generateButton")}
        </Button>
        {download.isPending && (
          <span className="text-xs text-ink-muted">{t("dossier.generatingHint")}</span>
        )}
        {status && !download.isPending && (
          <span data-testid="dossier-status" className={`text-xs ${statusColour}`}>
            {status.text}
          </span>
        )}
      </div>
    </Card>
  );
}
